<script>
    import {
        CardLayout,
        UnsetPatientBadDebtorByMedicareModal,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getBoolean,
        getFormatBirthDate,
        getGenderType,
        getOpvStatusDVA,
        getOpvStatusHealthFund,
        getOpvStatusMedicare,
        getText,
        getTitle,
        goToURL,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import PatientInsuranceDetails from "../../PatientEncounter/components/PatientInsuranceDetails.svelte";

    export let patientDetails = {};
    export let patientEncounterDetails = {};
    export let loadData = () => {};

    let updatePatientBadDebtorByMedicareModalOpen = false,
        isUpdatingPatientBadDebtorByMedicare = false;

    let updatePatientBadDebtorByMedicare = async () => {
        isUpdatingPatientBadDebtorByMedicare = true;

        await apiMethods.putMethod
            .updatePatient({
                ...patientDetails,
                badDebtorByMedicare: false,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to unset patient Bad Debtor recognised by Medicare Number, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    _closeConfirmBadDebtorByMedicareModal();
                    loadData();
                }
            });

        isUpdatingPatientBadDebtorByMedicare = false;
    };

    // HELPER FUNCTIONS
    const _getPatientFullName = () => {
        const { title, firstName, middleName, lastName } = patientDetails;

        return `${getTitle(title)} ${firstName} ${middleName} ${lastName}`;
    };

    const _getPatientAddress = () => {
        const { address, city, state, postCode } = patientDetails;

        const res = [];

        if (address) res.push(address);
        if (city) res.push(city);
        if (state) res.push(state);
        if (postCode) res.push(postCode);

        return res.join(", ");
    };

    const _redirectToPatient = () => {
        goToURL(`/Patient/view/${patientDetails.id}`);
    };

    const _openConfirmChangeBadDebtorByMedicareModal = () =>
        (updatePatientBadDebtorByMedicareModalOpen = true);

    const _closeConfirmBadDebtorByMedicareModal = () =>
        (updatePatientBadDebtorByMedicareModalOpen = false);
</script>

<CardLayout title="Patient Details">
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Full Name:</p>
                <p
                    class="content cursor-pointer underline font-bold"
                    on:click|preventDefault={_redirectToPatient}
                >
                    {_getPatientFullName()}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Gender:</p>
                <p class="content">{getGenderType(patientDetails.sex)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Date of Birth:</p>
                <p class="content">
                    {getFormatBirthDate(patientDetails.birthDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Phone:</p>
                <p class="content">{getText(patientDetails.phone)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Email:</p>
                {#if patientDetails.email}
                    <a
                        href="mailto:{patientDetails.email}"
                        target="_blank"
                        class="content break-all hover:text-[#8A3ED7] hover:underline hover:font-bold"
                    >
                        {getText(patientDetails.email)}
                    </a>
                {/if}
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Address:</p>
                <p class="content">{_getPatientAddress()}</p>
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-2">
            {#if patientDetails.badDebtor || patientDetails.badDebtorByMedicare}
                <div class="cursor-default badge badge-error">
                    {patientDetails.badDebtorByMedicare
                        ? "Bad Debtor recognised by Medicare Number"
                        : "Bad Debtor"}
                </div>
            {/if}
            <!-- 
            {#if patientDetails.badDebtorByMedicare}
                <div
                    class="badge badge-error cursor-pointer"
                    on:click={_openConfirmChangeBadDebtorByMedicareModal}
                >
                    Bad Debtor recognised by Medicare Number
                </div>
            {/if} -->

            <PatientInsuranceDetails
                {patientDetails}
                patientEncounter={patientEncounterDetails}
            />
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            {#if patientDetails.insuranceType == 20}
                <div class="flex flex-row gap-x-2">
                    <p class="title">DVA Verification Status:</p>
                    <p
                        class={`content ${
                            patientDetails.opvStatusVeteran == 1
                                ? "text-greenDefault"
                                : patientDetails.opvStatusVeteran == 2 ||
                                    patientDetails.opvStatusVeteran == 3
                                  ? "text-red-400"
                                  : ""
                        }`}
                    >
                        {getOpvStatusDVA(patientDetails.opvStatusVeteran)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Verification manually approved:</p>
                    <p class="content">
                        {getBoolean(patientDetails.opvManuallyApproved)}
                    </p>
                </div>
            {:else}
                <div class="flex flex-row gap-x-2">
                    <p class="title">OPV Status - Medicare:</p>
                    <p
                        class={`content ${
                            patientDetails.opvStatusMedicare == 1
                                ? "text-greenDefault"
                                : patientDetails.opvStatusMedicare == 2 ||
                                    patientDetails.opvStatusMedicare == 3
                                  ? "text-red-400"
                                  : ""
                        }`}
                    >
                        {getOpvStatusMedicare(patientDetails.opvStatusMedicare)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">OPV Status - Health Fund:</p>
                    <p
                        class={`content ${
                            patientDetails.opvStatusHealthFund == 1
                                ? "text-greenDefault"
                                : patientDetails.opvStatusHealthFund == 2 ||
                                    patientDetails.opvStatusHealthFund == 3
                                  ? "text-red-400"
                                  : ""
                        }`}
                    >
                        {getOpvStatusHealthFund(
                            patientDetails.opvStatusHealthFund,
                        )}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Verification manually approved:</p>
                    <p class="content">
                        {getBoolean(patientDetails.opvManuallyApproved)}
                    </p>
                </div>
            {/if}
        </div>
    </div>
</CardLayout>

<UnsetPatientBadDebtorByMedicareModal
    bind:isModalOpen={updatePatientBadDebtorByMedicareModalOpen}
    isConfirming={isUpdatingPatientBadDebtorByMedicare}
    closeModal={_closeConfirmBadDebtorByMedicareModal}
    confirmAction={updatePatientBadDebtorByMedicare}
/>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }

    .transition {
        transition: 0.5s linear;
    }
</style>
